import bindAllOfThis from "src/utils/BindThisHelper";
import ImageApi from 'src/utils/ImageHelper';
import { TiledGameObject } from "src/games/shared/phaserHelpers/HandyTypes";
import { PhaserButtonConfig, PhaserButton } from "src/games/shared/Button";
import TranslateApi from "src/utils/TranslateHelper";
import initializeInteractiveCursor from "src/games/shared/InteractiveCursorUtil";
import { Color } from "src/games/shared/ColorValue";
import { EndGameScene } from "src/games/CollageGame/scenes/EndGameScene";
import { TextItem, ImageItem } from "../objects/Item";
import AudioTranslations from "src/constants/AudioTranslations";

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    background: Phaser.GameObjects.Image;
    map: Phaser.Tilemaps.Tilemap;
    resizing: boolean = false;
    rotating: boolean = false;
    resizeIcon: Phaser.GameObjects.Image;
    rotateCWIcon: Phaser.GameObjects.Container;
    rotateCCWIcon: Phaser.GameObjects.Container;
    resetIcon: Phaser.GameObjects.Container;
    deleteIcon: Phaser.GameObjects.Container;
    soundIcon: Phaser.GameObjects.Container;
    corner: string;
    currentItem: ImageItem | TextItem;
    iconGroup: Phaser.GameObjects.Group;
    rotation: number = 0;
    items: any[];
    canvas: Phaser.Geom.Rectangle;
    gameOver: boolean = false;
    textIds: Set<number>;
    rock: Phaser.Geom.Rectangle;
    textArea: Phaser.Geom.Rectangle;
    textItems: TextItem[];

    public static CROPPED_IMAGE: string = null;
    private NUMBER_OF_TEXT_ASSETS: number = 4;
    // Must be same length as above
    private textItemPositions = [
        { x: 150, y: 200 },
        { x: 400, y: 175 },
        { x: 150, y: 300 },
        { x: 400, y: 325 }
    ];

    constructor() {
        super({
            key: "MainScene" + Math.random()
        });
        bindAllOfThis(this, MainScene.prototype);
    }

    preload(): void {
        this.load.image("bg", require("/src/assets/qci/backgrounds/collage.jpg"));
        this.load.image("resize", require("/src/assets/qci/sprites/resize_icon.png"));
        this.load.image("rotate", require("/src/assets/qci/sprites/rotate_icon.png"));
        this.load.image("reset", require("/src/assets/qci/sprites/reset_icon.png"));
        this.load.image("delete", require("/src/assets/qci/sprites/delete_icon.png"));
        this.load.image("collage_1", require("/src/assets/qci/sprites/collageGame/collage_1.jpg"));
        this.load.image("collage_2", require("/src/assets/qci/sprites/collageGame/collage_2.jpg"));
        this.load.image("collage_3", require("/src/assets/qci/sprites/collageGame/collage_3.jpg"));
        this.load.image("collage_4", require("/src/assets/qci/sprites/collageGame/collage_4.jpg"));
        this.load.image("collage_5", require("/src/assets/qci/sprites/collageGame/collage_5.jpg"));
        this.load.image("collage_6", require("/src/assets/qci/sprites/collageGame/collage_6.jpg"));
        this.load.image("collage_7", require("/src/assets/qci/sprites/collageGame/collage_7.jpg"));
        this.load.tilemapTiledJSON("map", require("/src/assets/qci/maps/collage.json"));

        var keys = [];

        for (var i = 1; i < 21; i++) {
            keys.push('Collage.Phaser.CollageAssets.' + i);
        }

        keys.forEach((key) => {
            this.load.audio(key, AudioTranslations.FindByKeyAndLanguage(key, TranslateApi.currentLanguage.code).url);
        });

    }

    create(): void {
        this.initialize();
        this.generateScene();
        this.registerEvents();
        initializeInteractiveCursor(this);
    }

    initialize() {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: "map" });
        this.items = [];
        this.textItems = [];
        this.textIds = new Set<number>();
    }

    registerEvents() {
        this.input.on('dragstart', (pointer: Phaser.Input.Pointer, gameObject: any) => {
            if (!this.gameOver) {
                this.children.bringToTop(gameObject);
                // If they're touching the image itself, then the current item is the gameObject
                // Otherwise, we don't change the current item to the gameObject (resize icon in this case), because they're just trying to resize the image
                if (gameObject.name !== "resize") {
                    this.currentItem = gameObject;
                }
                this.setIconGroupVisible(false);
                var corner = new Phaser.Geom.Point(pointer.downX, pointer.downY);
                var center = this.currentItem.getCenter();
                if (corner.x >= center.x && corner.y >= center.y) { // right and below center
                    this.corner = "bottomRight";
                }
                else if (corner.x >= center.x && corner.y <= center.y) { // right and above center
                    this.corner = "topRight";
                }
                else if (corner.x <= center.x && corner.y <= center.y) { // left and above center
                    this.corner = "topLeft";
                }
                else if (corner.x <= center.x && corner.y >= center.y) { // left and below center
                    this.corner = "bottomLeft";
                }
            }
        });

        this.input.on('drag', (pointer: Phaser.Input.Pointer, gameObject: any, dragX: number, dragY: number) => {
            if (!this.gameOver) {
                if (this.resizing) {
                    var corner = this.getBottomRightCorner();

                    if (this.corner === "bottomRight") {
                        this.resizeForBottomRightCorner(pointer, corner);
                    }
                    else if (this.corner === "topRight") {
                        this.resizeForTopRightCorner(pointer, corner);
                    }
                    else if (this.corner === "topLeft") {
                        this.resizeForTopLeftCorner(pointer, corner);
                    }
                    else if (this.corner === "bottomLeft") {
                        this.resizeForBottomLeftCorner(pointer, corner);
                    }
                }
                else {
                    gameObject.x = dragX;
                    gameObject.y = dragY;
                }
            }
        });

        this.input.on('dragend', (pointer: Phaser.Input.Pointer, gameObject: any, dropped: boolean) => {
            if (!this.gameOver) {
                this.resizing = false;
                this.rotating = false;
                this.setIconGroupVisible(true);
            }
        });
    }

    generateScene(): void {
        this.background = this.add.image(0, 0, "bg");
        this.background.setOrigin(0);
        this.background.setDisplaySize(this.gameWidth, this.gameHeight);

        this.addObjects();
        this.addTextItems();
        this.iconGroup = this.add.group();
        this.addResizeIcon();
        this.addToolIcons();
        this.setIconGroupVisible(true);
    }

    addResizeIcon(): void {
        this.resizeIcon = this.add.image(-100, 0, "resize");
        this.resizeIcon.setOrigin(0);
        this.resizeIcon.setDepth(10);
        this.resizeIcon.setName("resize");
        this.resizeIcon.setDisplaySize(30, 30);
        this.resizeIcon.setInteractive();
        this.resizeIcon.on('pointerdown', () => {
            if (!this.gameOver) {
                this.resizing = true;
                this.children.bringToTop(this.currentItem);
            }
        });
        this.input.setDraggable(this.resizeIcon);
    }

    createIcon(texture: string, flip?: boolean): Phaser.GameObjects.Container {
        var container = this.add.container(-100, 50);
        var background = this.add.image(0, 0, "pixel");
        background.setTint(0xffffff);
        background.setDisplaySize(30, 30);
        container.add(background);

        var icon = this.add.image(0, 0, texture);
        icon.setDisplaySize(30, 30);
        icon.setDepth(2);
        if (flip) {
            icon.setFlipX(true);
        }

        container.add(icon);
        container.setSize(30, 30);
        container.setInteractive();
        return container;
    }

    addToolIcons(): void {
        this.rotateCWIcon = this.createIcon("rotate");
        this.rotateCWIcon.on('pointerdown', () => {
            if (!this.gameOver) {
                this.rotating = true;
                this.resizing = false;
                this.children.bringToTop(this.currentItem);
                this.currentItem.angle += 10;
            }
        });

        this.rotateCCWIcon = this.createIcon("rotate", true);
        this.rotateCCWIcon.on('pointerdown', () => {
            if (!this.gameOver) {
                this.rotating = true;
                this.resizing = false;
                this.children.bringToTop(this.currentItem);
                this.currentItem.angle -= 10;
            }
        });

        this.resetIcon = this.createIcon("reset");
        this.resetIcon.on('pointerdown', () => {
            if (!this.gameOver) {
                this.resetImageInPlace();
            }
        });

        this.deleteIcon = this.createIcon("delete");
        this.deleteIcon.on('pointerdown', () => {
            if (!this.gameOver) {
                var eases = [Phaser.Math.Easing.Back.InOut, Phaser.Math.Easing.Bounce.Out];
                this.putImageBackOnRock(eases[Phaser.Math.Between(0, 1)]);
            }
        });

        this.iconGroup.add(this.rotateCCWIcon);
        this.iconGroup.add(this.rotateCWIcon);
        this.iconGroup.add(this.resetIcon);
        this.iconGroup.add(this.deleteIcon);
    }

    /**
     * Resets the image to the starting size and no rotation
     */
    resetImageInPlace(): void {
        this.resizing = false;
        this.children.bringToTop(this.currentItem);
        this.currentItem.angle = 0;
        var startingSize = this.currentItem.startingSize;
        this.currentItem.setDisplaySize(startingSize.x, startingSize.y);
    }

    /**
     * Puts the image back on the rock the way it started (angle and size)
     */
    putImageBackOnRock(ease): void {
        this.resizing = false;
        if (this.currentItem != null) {
            this.throwImageToTheGround(this.currentItem, ease);
        }
    }

    addObjects(): void {
        var index = 1;
        var config: PhaserButtonConfig = {
            backgroundColor: Color.KUDER_SECONDARY_BUTTON,
            smartSize: true
        };

        this.map.getObjectLayer("Objects").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "canvas") {
                this.canvas = new Phaser.Geom.Rectangle(ob.x, ob.y, ob.width, ob.height);
            }
            else if (ob.type === "item") {
                this.addItem(ob, index);
                index++;
            }
            else if (ob.name === "done") {
                var doneButton = new PhaserButton(this, this.gameWidth - ob.width - 50, ob.y, ob.width, ob.height, TranslateApi.translate("common.done"), config).setDepth(1);
                doneButton.setX(this.gameWidth - doneButton.width - 50);
                doneButton.on('buttonClicked', () => {
                    // Screenshot for later
                    this.registry.parent.renderer.snapshot((image: HTMLImageElement) => {
                        image.onload = () => {
                            var croppedImage = ImageApi.CropImageToDataURI(image, this.canvas.x, this.canvas.y, this.canvas.width, this.canvas.height);
                            console.warn("Screenshot!", croppedImage); // JB - Remove this console.warn
                            this.endGame(croppedImage);
                        };
                    });
                });
            }
            else if (ob.name === "restart") {
                var restartButton = new PhaserButton(this, this.gameWidth - ob.width - 50, ob.y, ob.width, ob.height, TranslateApi.translate("Collage.Phaser.reset"), config);
                restartButton.setX(this.gameWidth - restartButton.width - 50);
                restartButton.on('buttonClicked', () => {
                    if (!this.gameOver) {
                        this.restartGame();
                    }
                });
            }
            else if (ob.name === "moreText") {
                var swapTextButton = new PhaserButton(this, ob.x, ob.y, ob.width, ob.height, TranslateApi.translate("Collage.Phaser.swapText"), config);
                swapTextButton.on('buttonClicked', () => {
                    if (!this.gameOver) {
                        this.swapTextItems();
                    }
                });
            }
            else if (ob.name === "rock") {
                this.rock = new Phaser.Geom.Rectangle(ob.x, ob.y, ob.width, ob.height);
            }
            else if (ob.name === "textArea") {
                this.textArea = new Phaser.Geom.Rectangle(ob.x, ob.y, ob.width, ob.height);
            }
        });

        this.map.getObjectLayer("Astro").objects.forEach((ob: TiledGameObject) => {
            var astro = this.add.image(ob.x, ob.y, "pilots", "a");
            astro.setOrigin(0);
            astro.setDisplaySize(ob.width, ob.height);
        });
    }

    addItem(ob: TiledGameObject, index: number): void {
        var x = ob.x + ob.width / 2;
        var y = ob.y + ob.height / 2;
        var item = new ImageItem(this, x, y, "collage_" + index);
        item.setDisplaySize(item.width * 0.25, item.height * 0.25);
        item.setAngle(ob.rotation);
        item.setInteractive();
        item.startingSize = new Phaser.Math.Vector2(item.displayWidth, item.displayHeight);
        item.startingPosition = item.getBounds();
        item.startingAngle = item.angle;
        this.items.push(item);
        this.input.setDraggable(item);
    }

    swapTextItems() {
        // For items not on the canvas, swap them out with new ones
        this.textItems.filter(i => !this.IsOnCanvas(i)).forEach((textItem: TextItem) => {
            var currentId = textItem.textId;
            var center = textItem.getCenter();
            var currentPos = {
                x: center.x,
                y: center.y
            };
            // Destroy the current item
            this.deleteTextItem(textItem);

            // Make sure get a unique id (textIds is a set)
            var newId = Phaser.Math.Between(1, 20);
            while (this.textIds.has(newId)) {
                newId = Phaser.Math.Between(1, 20);
            }

            // Replace current id with new one
            this.textIds.delete(currentId);
            this.textIds.add(newId);
            var newTextItem = this.createTextItem(newId, currentPos);
        });
    }

    deleteTextItem(textItem: TextItem) {
        // Remove from textItem array
        var index = this.textItems.findIndex((i) => i.textId === textItem.textId);
        this.textItems.splice(index, 1);

        // Remove from items array
        index = this.items.findIndex((i) => i.type === "TextItem" && i.textId === textItem.textId);
        this.items.splice(index, 1);

        // Destroy the actual text object
        textItem.destroy();
    }

    createTextItem(id: number, position): TextItem {
        var textItem = new TextItem(this, 0, 0, id);
        textItem.setPosition(position.x, position.y);
        textItem.startingPosition = textItem.getBounds();
        this.textItems.push(textItem);
        this.items.push(textItem);
        return textItem;
    }

    addTextItems(): void {
        // Choose random numbers corresponding to Kuder's text assets to use as collage items
        this.textIds = new Set<number>();
        while (this.textIds.size < this.NUMBER_OF_TEXT_ASSETS) {
            this.textIds.add(Phaser.Math.Between(1, 20));
        }
        this.textIds.forEach((id: number) => {
            var position = this.textItemPositions.pop();
            var item = this.createTextItem(id, position);
        });
    }

    update(): void {
        if (!this.gameOver) {
            this.updateToolGroup();
        }
    }

    updateToolGroup(): void {
        if (this.currentItem == null) {
            return;
        }

        var bottomRight = this.getBottomRightCorner();

        // Make sure that the resize icon doesn't overlap with the image (happens when rotating)
        this.resizeIcon.setPosition(bottomRight.x, bottomRight.y);
        if (Phaser.Geom.Intersects.RectangleToRectangle(this.resizeIcon.getBounds(), this.currentItem.getBounds())) {
            if (this.resizeIcon.x < this.currentItem.x) {
                this.resizeIcon.x -= 30;
                this.resizeIcon.y -= 30;
            }
        }

        var rightOfImageX = this.currentItem.getCenter().x + Math.max(this.currentItem.displayWidth / 2, this.currentItem.displayHeight / 2) + 40;
        var topOfImageY = this.currentItem.getCenter().y - Math.min(this.currentItem.displayWidth / 2, this.currentItem.displayHeight / 2);

        Phaser.Actions.GridAlign(this.iconGroup.getChildren(), {
            x: rightOfImageX,
            y: topOfImageY,
            width: 4,
            height: 1,
            cellWidth: 30,
            cellHeight: 30,
        });


        if (this.resizing) {
            this.resizeIcon.setAlpha(0.3);
        }
        else {
            this.resizeIcon.setAlpha(1);
        }

        this.children.bringToTop(this.resizeIcon);

        // Bring the icons to the top so you can always see them
        this.iconGroup.getChildren().forEach(element => {
            this.children.bringToTop(element);
        });

    }

    getBottomRightCorner(): Phaser.Geom.Point {
        return this.currentItem.getBottomRight();
    }

    getMaxHeight(): number {
        return this.currentItem.type === "TextItem" ? 400 : this.currentItem.height * 0.8;
    }

    getMaxWidth(): number {
        return this.currentItem.type === "TextItem" ? 400 : this.currentItem.width * 0.8;
    }

    getMinHeight(): number {
        return this.currentItem.type === "TextItem" ? 10 : this.currentItem.height * 0.1;
    }

    getMinWidth(): number {
        return this.currentItem.type === "TextItem" ? 10 : this.currentItem.width * 0.1;
    }

    getDeltaWidth(): number {
        return this.currentItem.displayWidth * 0.03;
    }

    getDeltaHeight(): number {
        return this.currentItem.displayHeight * 0.03;
    }

    resizeForBottomRightCorner(pointer: Phaser.Input.Pointer, corner: Phaser.Geom.Point): void {
        // Dragging right and down (increase size)
        if (pointer.x > corner.x && pointer.y > corner.y) {
            if (((this.currentItem.displayWidth + this.getDeltaWidth()) <= this.getMaxWidth()) &&
                ((this.currentItem.displayHeight + this.getDeltaHeight()) <= this.getMaxHeight())) {
                this.currentItem.displayWidth += this.getDeltaWidth();
                this.currentItem.displayHeight += this.getDeltaHeight();
            }
        }

        // Dragging left and up (decrease size)
        if (pointer.x < corner.x && pointer.y < corner.y) {
            if (((this.currentItem.displayWidth - this.getDeltaWidth()) >= this.getMinWidth()) &&
                ((this.currentItem.displayHeight - this.getDeltaHeight()) >= this.getMinHeight())) {
                this.currentItem.displayWidth -= this.getDeltaWidth();
                this.currentItem.displayHeight -= this.getDeltaHeight();
            }
        }
    }

    resizeForTopRightCorner(pointer: Phaser.Input.Pointer, corner: Phaser.Geom.Point): void {
        // Dragging right and up (increase size)
        if (pointer.x > corner.x && pointer.y < corner.y) {
            if (((this.currentItem.displayHeight + this.getDeltaHeight()) <= this.getMaxHeight()) &&
                ((this.currentItem.displayWidth + this.getDeltaWidth()) <= this.getMaxWidth())) {
                this.currentItem.displayWidth += this.getDeltaWidth();
                this.currentItem.displayHeight += this.getDeltaHeight();
            }
        }

        // Dragging left and down (decrease size)
        if (pointer.x < corner.x && pointer.y > corner.y) {
            if (((this.currentItem.displayHeight - this.getDeltaHeight()) >= this.getMinHeight()) &&
                ((this.currentItem.displayWidth - this.getDeltaWidth()) >= this.getMinWidth())) {
                this.currentItem.displayWidth -= this.getDeltaWidth();
                this.currentItem.displayHeight -= this.getDeltaHeight();
            }
        }
    }

    resizeForTopLeftCorner(pointer: Phaser.Input.Pointer, corner: Phaser.Geom.Point): void {
        // Dragging left and up (increase size)
        if (pointer.x < corner.x && pointer.y < corner.y) {
            if (((this.currentItem.displayHeight + this.getDeltaHeight()) <= this.getMaxHeight()) &&
                ((this.currentItem.displayWidth + this.getDeltaWidth()) <= this.getMaxWidth())) {
                this.currentItem.displayWidth += this.getDeltaWidth();
                this.currentItem.displayHeight += this.getDeltaHeight();
            }
        }

        // Dragging right and down (decrease size)
        if (pointer.x > corner.x && pointer.y > corner.y) {
            if (((this.currentItem.displayHeight - this.getDeltaHeight()) >= this.getMinHeight()) &&
                ((this.currentItem.displayWidth - this.getDeltaWidth()) >= this.getMinWidth())) {
                this.currentItem.displayWidth -= this.getDeltaWidth();
                this.currentItem.displayHeight -= this.getDeltaHeight();
            }
        }
    }

    resizeForBottomLeftCorner(pointer: Phaser.Input.Pointer, corner: Phaser.Geom.Point): void {
        // Dragging left and down (increase size)
        if (pointer.x < corner.x && pointer.y > corner.y) {
            if (((this.currentItem.displayWidth + this.getDeltaWidth()) <= this.getMaxWidth()) &&
                ((this.currentItem.displayHeight + this.getDeltaHeight()) <= this.getMaxHeight())) {
                this.currentItem.displayWidth += this.getDeltaWidth();
                this.currentItem.displayHeight += this.getDeltaHeight();
            }
        }

        // Dragging right and up (decrease size)
        if (pointer.x > corner.x && pointer.y < corner.y) {
            if (((this.currentItem.displayWidth - this.getDeltaWidth()) >= this.getMinWidth()) &&
                ((this.currentItem.displayHeight - this.getDeltaHeight()) >= this.getMinHeight())) {
                this.currentItem.displayWidth -= this.getDeltaWidth();
                this.currentItem.displayHeight -= this.getDeltaHeight();
            }
        }
    }

    private setIconGroupVisible(visible): void {
        this.iconGroup.getChildren().forEach((im: Phaser.GameObjects.Image) => {
            im.setVisible(visible);
        });
    }

    endGame(image: string): void {
        this.sound.stopAll();
        this.gameOver = true;
        MainScene.CROPPED_IMAGE = image;

        this.input.setDefaultCursor("default");
        this.input.removeAllListeners();
        this.scene.add("EndGameScene", EndGameScene, true);
        this.scene.remove(this);
    }

    /**
     * Tween/animation to put image back to the rock
     */
    private throwImageToTheGround(target: any, ease): void {
        var startingSize = target.startingSize;
        var startingPosition = target.startingPosition;
        var startingAngle = target.startingAngle;

        this.tweens.add({
            targets: target,
            duration: 1000,
            ease: ease,
            x: startingPosition.x + startingPosition.width / 2,
            y: startingPosition.y + startingPosition.height / 2,
            angle: startingAngle,
            displayWidth: startingSize.x,
            displayHeight: startingSize.y,
        });
    }

    restartGame(): void {
        var eases = [Phaser.Math.Easing.Back.InOut, Phaser.Math.Easing.Bounce.Out];
        var ease = eases[Phaser.Math.Between(0, 1)];
        this.items.forEach((item) => {
            this.currentItem = item;
            this.putImageBackOnRock(ease);
        });
        this.currentItem = null;
        this.resizeIcon.setX(-100);
        this.setIconGroupVisible(false);
    }

    private IsOnCanvas(item: TextItem): boolean {
        return Phaser.Geom.Intersects.RectangleToRectangle(this.canvas, item.getBounds());
    }
}



// WEBPACK FOOTER //
// ./src/games/CollageGame/scenes/MainScene.ts